import { useState, useMemo } from 'react';
import { Button } from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { Modal } from '@fortress-technology-solutions/fortress-component-library/Molecules_Fortress';
import { Form } from '@fortress-technology-solutions/fortress-component-library/Organisms';
import { UploadIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useIntl from '../../../hooks/useIntl';
import { getFormFields } from './constants';
import messages from './messages';
import ConditionalTooltip from '../../../components/ConditionalTooltip';

interface Props {
  title: string;
  description: string;
  confirmLabel: string;
  buttonText: string;
  buttonTooltip: string;
  submitButtonText: string;
  onSend: (wassId: string, password: string) => void | Promise<void>;
  disabled: boolean;
}

function SubmitToTracsModal({
  title,
  description,
  confirmLabel,
  buttonText,
  buttonTooltip,
  submitButtonText,
  onSend,
  disabled,
}: Props) {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = useMemo(
    () => ({
      wassId: '',
      password: '',
      confirm: false,
    }),
    [],
  );

  const reactHookFormProps = useReactHookFormProps({
    defaultValues,
    mode: 'onBlur',
  });

  const confirm = reactHookFormProps.watch('confirm');
  const wassId = reactHookFormProps.watch('wassId');
  const password = reactHookFormProps.watch('password');

  const handleClose = () => {
    setOpen(false);
    reactHookFormProps.reset(defaultValues);
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
    try {
      const result = onSend(data.wassId, data.password);
      await Promise.resolve(result);
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const formFields = useMemo(
    () => getFormFields(messages, intl, confirmLabel, description),
    [intl, confirmLabel, description],
  );

  const isSubmitDisabled = !confirm || !wassId || !password || isSubmitting;

  return (
    <>
      <ConditionalTooltip show={disabled} title={buttonTooltip} placement="top">
        <Button
          disabled={disabled}
          variant="shout"
          startIcon={<UploadIcon />}
          onClick={() => setOpen(true)}
        >
          {buttonText}
        </Button>
      </ConditionalTooltip>
      <Modal
        open={open}
        onClose={isSubmitting ? undefined : handleClose}
        title={title}
        ModalProps={{
          PaperProps: {
            sx: {
              width: {
                xs: 'calc(100% - 32px)',
                sm: 480,
              },
              height: 500,
            },
          },
        }}
        actionsProps={[
          {
            children: intl.formatMessage(messages.cancel),
            onClick: handleClose,
            disabled: isSubmitting,
          },
          {
            children: submitButtonText,
            submitButton: true,
            disabled: isSubmitDisabled,
            isSubmitting,
            onClick: reactHookFormProps.handleSubmit(handleSubmit),
          },
        ]}
      >
        <Form ReactHookFormProps={reactHookFormProps} fields={formFields} />
      </Modal>
    </>
  );
}

export default SubmitToTracsModal;
